import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type AppRole =
  | "admin"
  | "member"
  | "events_admin"
  | "blog_admin"
  | "member_admin"
  | "applicant_admin"
  | "visit_admin";

// Roles that unlock at least one section of the Admin dashboard.
// blog_admin is excluded: Stories are managed from the member portal.
export const ADMIN_ROLES: AppRole[] = [
  "admin",
  "events_admin",
  "member_admin",
  "applicant_admin",
  "visit_admin",
];

/**
 * Loads all roles granted to the signed-in user in a single query.
 * Server-side RLS remains the real enforcement layer; this only drives UI gating.
 */
export const useMyRoles = () => {
  const [roles, setRoles] = useState<Set<AppRole>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async (userId?: string) => {
      if (!userId) {
        if (cancelled) return;
        setRoles(new Set());
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId);

        if (!cancelled) {
          setRoles(
            error || !data
              ? new Set<AppRole>()
              : new Set(data.map((r) => r.role as AppRole))
          );
        }
      } catch {
        if (!cancelled) setRoles(new Set());
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => load(session?.user.id))
      .catch(() => {
        if (!cancelled) {
          setRoles(new Set());
          setLoading(false);
        }
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setTimeout(() => load(session?.user.id), 0);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const isAdmin = roles.has("admin");
  const hasAnyAdminRole = ADMIN_ROLES.some((r) => roles.has(r));

  const has = (role: AppRole) => isAdmin || roles.has(role);

  return { roles, isAdmin, hasAnyAdminRole, has, loading };
};
